// frontend/src/components/HeatmapModal/HeatmapModal.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ImageHeatmap from './ImageHeatmap'; // Corrected relative path
import './HeatmapModal.css';

const HeatmapModal = ({ isOpen, onClose, imageSrc, regions, taskName, score, mode = 'full', footerNote, contentType }) => {
  const { t } = useTranslation();
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [calculatedDims, setCalculatedDims] = useState({ width: 'auto', height: 'auto' });

  // --- NEW useEffect to calculate dimensions ---
  useEffect(() => {
    // Only run this logic for history mode and if we have an image source
    if (mode === 'history' && imageSrc) {
      const img = new Image();
      img.onload = () => {
        const { naturalWidth, naturalHeight } = img;
        const targetSize = 300; // The size of the largest dimension
        let newWidth, newHeight;

        // Apply the "smart resize" logic
        if (naturalWidth >= naturalHeight) {
          // Landscape or square image
          newWidth = targetSize;
          newHeight = (naturalHeight / naturalWidth) * targetSize;
        } else {
          // Portrait image
          newHeight = targetSize;
          newWidth = (naturalWidth / naturalHeight) * targetSize;
        }

        setCalculatedDims({ width: `${newWidth}px`, height: `${newHeight}px` });
      };
      img.src = imageSrc; // This triggers the onload event
    } else {
        // Reset dimensions if not in history mode
        setCalculatedDims({ width: 'auto', height: 'auto' });
    }
  }, [imageSrc, mode]); // Rerun when the image or mode changes

  if (!isOpen) {
    return null;
  }

  // Use the 'mode' prop to dynamically add a CSS class.
  const wrapperClassName = `media-wrapper-modal ${mode === 'history' ? 'history-mode' : ''}`;
  const modalTitle = (score !== null && score !== undefined)
    ? t('heatmap.modalTitle', { taskName: taskName, score: score })
    : t('heatmap.modalTitleNoScore', { taskName: taskName });

  const modalContent = (
    <div
      className={`heatmap-modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="heatmap-modal-title"
    >
      <div className="heatmap-modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="heatmap-modal-header">
          {/* Use the new dynamic title */}
          <h2 id="heatmap-modal-title">{modalTitle}</h2>
          <IconButton
            aria-label={t('heatmap.closeButtonLabel')}
            onClick={onClose}
            className="heatmap-modal-close-button"
          >
            <CloseIcon />
          </IconButton>
        </header>

        <div className="heatmap-modal-body">
          {/* Apply dynamic class AND the new dynamic inline styles */}
          <div 
            className={wrapperClassName}
            style={mode === 'history' ? calculatedDims : {}}
          >
            <img src={imageSrc} alt="Analyzed content" />
            {showHeatmap && (
                <ImageHeatmap 
                    regions={regions} 
                    imageSrc={imageSrc} 
                    mode={mode}
                />
            )}
          </div>
        </div>

        <footer className="heatmap-modal-footer">
          {/* Case 1: A specific note was passed (e.g., for video on main UI) */}
          {footerNote && (
            <p className="history-mode-note footer-note">{footerNote}</p>
          )}
          {/* This button will now ONLY render when mode is 'full' */}
          {regions && regions.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={showHeatmap ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowHeatmap(!showHeatmap)}
              className="heatmap-toggle-button"
            >
              {showHeatmap ? t('heatmap.hideHeatmap') : t('heatmap.showHeatmap')}
            </Button>
          )}

          {regions && regions.length > 0 && (
            <p className="accuracy-disclaimer-note">
              {t('heatmap.disclaimer')}
            </p>
          )}

          {(mode === 'history' || contentType === 'video') && (
            <p className="history-mode-note">
              <strong>Note:</strong> {t('heatmap.privacyNote')}
            </p>
          )}
        </footer>

      </div>
    </div>
  );
  
  return ReactDOM.createPortal(modalContent, document.body);
};

export default HeatmapModal;